import classNames from 'classnames';
import { Quad } from 'gsap';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import useRouterPush from '../../../hooks/useRouterPush';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import Heading from '../../atoms/heading/Heading';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import CaseHeader from '../case-header/CaseHeader';
import type { NextCaseProps, NextCaseRef } from './NextCase.data';
import styles from './NextCase.module.scss';
import { setupTransitionOutTimeline, showLoader } from './NextCase.transitions';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import { stripLocaleFromUrl } from '../../../util/url.utils';
import { useRouter } from 'next/router';
import { isDesktop } from '../../../hooks/useIsDesktopDevice';

function NextCase({
  blok,
  full_slug,
  className,
  canTransitionIn,
  ref,
}: NextCaseProps & { ref?: Ref<NextCaseRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const contentRef = useRef(null);
  const caseHeaderRef = useRef<HTMLElement>(null);
  const routerPush = useRouterPush();
  const router = useRouter();
  const nextSlug = `${router.locale}/${stripLocaleFromUrl(full_slug)}`;
  const isLeavingPage = useRef(false);
  const loaderRef = useRef<HTMLDivElement>(null);
  const { lock, scaleDelta, resetScaleDelta } =
    useScrollProviderContext() as ScrollProviderContextType;
  const { inView } = useScrollTrigger({
    trigger: caseHeaderRef,
    vars: {
      start: 'top 20%',
    },
    enableInView: true,
  });

  let accentColor;
  if (blok?.accent_color_foreground?.color && blok?.accent_color_background?.color) {
    accentColor = {
      foreground: blok.accent_color_foreground.color,
      background: blok.accent_color_background.color,
    };
  }

  useThemeScrollBlock(elementRef, blok.has_header_accent, accentColor, true);

  useScrollTrigger({
    trigger: caseHeaderRef,
    vars: {
      scrub: true,
      start: '200 bottom',
      end: 'top top',
      // Make the scrolling slower/harder when 'smooth-scroll' is used.
      onLeaveBack() {
        resetScaleDelta();
      },
      onEnter() {
        scaleDelta();
      },
    },
  });

  useScrollTrigger({
    trigger: caseHeaderRef,
    endTrigger: caseHeaderRef,
    animation: useSetupTimeline(setupTransitionOutTimeline, {
      contentRef,
    }),
    vars: {
      scrub: true,
      start: 'top 20%',
      end: 'top top',
    },
  });

  const { scrollTo, hasGsapScrollSmootherRef, scrollStatusRef } =
    useScrollProviderContext() as ScrollProviderContextType;
  useUpdateEffect(() => {
    if (inView && canTransitionIn && !isLeavingPage.current) {
      isLeavingPage.current = true;
      lock();

      setTimeout(() => {
        let y = 0;

        // If it's not using GsapScrollSmoother
        if (!hasGsapScrollSmootherRef.current) {
          let currentY = scrollStatusRef.current?.offset.y;
          if (typeof currentY !== 'number') {
            currentY = 0;
          }
          y = currentY + (caseHeaderRef.current?.getBoundingClientRect().top as number);
        }

        // Finish animation before changing route.
        scrollTo(0, y, 500, {
          target: caseHeaderRef.current,
          easing: Quad.easeInOut,
          callback: () => {
            if (!isDesktop) {
              showLoader(loaderRef);
            }

            setTimeout(
              () => {
                routerPush(nextSlug);
              },
              isDesktop ? 0 : 400,
            );
          },
        });
      }, 0);
    }
  }, [inView, canTransitionIn]);

  return (
    <div ref={elementRef} className={classNames(styles.nextCase, className)}>
      <div
        ref={contentRef}
        className={classNames(styles.content, 'grid-gutter', 'max-content-width')}
      >
        <Heading
          as="span"
          size="label-large"
          disableTriggerInOnScroll={false}
          className={styles.kanjiVertical}
        >
          {/* \u00a0 is the code for non-breakable space */}
          {blok?.vertical_text?.replace(' ', '\u00A0')}
        </Heading>
      </div>

      <CaseHeader
        ref={caseHeaderRef}
        blok={{ ...blok }}
        layoutState="previewNext"
        canTransitionIn={canTransitionIn}
        scrubTransitionIn
      />
      <div ref={loaderRef} className={classNames(styles.loader)} />
    </div>
  );
}

NextCase.displayName = 'NextCase';

export default NextCase;
